import api from './api';
import type { BrandDna, BrandDnaProfile, FontRole, ManualBrandDnaInput } from '../types/brandDna';

const BASE = '/marketing/brand-dna';
const CURRENT_KEY = 'snapfy_marketing_current_brand_dna';

/**
 * Eventos da marca atual (window): CHANGED sempre que ela muda (a tela abriu
 * outra marca, ou o cliente trocou); SWITCHED só quando o cliente troca pelo
 * seletor do menu — aí a tela aberta recarrega na marca nova.
 */
export const BRAND_CHANGED_EVENT = 'snapfy:marketing-brand-changed';
export const BRAND_SWITCHED_EVENT = 'snapfy:marketing-brand-switched';

type Envelope<T> = { data: T };

/**
 * DNA da marca (plataforma Marketing). A análise roda em segundo plano na API:
 * `start` devolve na hora com status "analyzing" e a tela acompanha com `get`.
 */
export const brandDnaService = {
  list: async (): Promise<BrandDna[]> => {
    const response = await api.get<Envelope<BrandDna[]>>(BASE);
    return response.data.data ?? [];
  },

  get: async (id: number): Promise<BrandDna> => {
    const response = await api.get<Envelope<BrandDna>>(`${BASE}/${id}`);
    return response.data.data;
  },

  /** "Novo cliente" → Analisar. */
  start: async (name: string, siteUrl: string): Promise<BrandDna> => {
    const response = await api.post<Envelope<BrandDna>>(BASE, { name, siteUrl });
    return response.data.data;
  },

  /** Salva o perfil inteiro (e/ou o nome); a API normaliza e recalcula o "DNA %". */
  update: async (id: number, changes: { name?: string; profile?: BrandDnaProfile }): Promise<BrandDna> => {
    const response = await api.put<Envelope<BrandDna>>(`${BASE}/${id}`, changes);
    return response.data.data;
  },

  /**
   * Site bloqueou a leitura: o cliente descreve a marca e o DNA fica pronto
   * (a análise de mercado roda de novo a partir dele).
   */
  manual: async (id: number, input: ManualBrandDnaInput): Promise<BrandDna> => {
    const response = await api.post<Envelope<BrandDna>>(`${BASE}/${id}/manual`, input);
    return response.data.data;
  },

  reanalyze: async (id: number): Promise<BrandDna> => {
    const response = await api.post<Envelope<BrandDna>>(`${BASE}/${id}/reanalyze`);
    return response.data.data;
  },

  /** "Enviar fonte (.otf/.ttf)" — vira a fonte do papel e tira o alerta. */
  uploadFont: async (id: number, role: FontRole, file: File): Promise<BrandDna> => {
    const form = new FormData();
    form.append('font', file);
    form.append('role', role);
    const response = await api.post<Envelope<BrandDna>>(`${BASE}/${id}/fonts`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`${BASE}/${id}`);
  },

  /** Marca (cliente) aberta por último na plataforma Marketing. */
  getCurrentId: (): number | null => {
    try {
      const raw = localStorage.getItem(CURRENT_KEY);
      const id = raw ? Number(raw) : NaN;
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  },

  setCurrentId: (id: number): void => {
    const changed = brandDnaService.getCurrentId() !== id;
    try {
      localStorage.setItem(CURRENT_KEY, String(id));
    } catch {
      /* storage indisponível */
    }
    if (changed) window.dispatchEvent(new CustomEvent(BRAND_CHANGED_EVENT, { detail: id }));
  },

  /** Troca pelo seletor do menu: grava a marca e avisa a tela aberta para recarregar nela. */
  switchTo: (id: number): void => {
    brandDnaService.setCurrentId(id);
    window.dispatchEvent(new CustomEvent(BRAND_SWITCHED_EVENT, { detail: id }));
  },
};
